import { useRef, useState } from "react";
import type { CurrencyCode } from "@shared/money";
import { formatMinor } from "@shared/money";
import { lineTotalMinor, type CartLine } from "./cart";

/** Past this many pixels of leftward drag, releasing removes the line (F-402). */
export const SWIPE_REMOVE_PX = 88;

function SwipeLine(props: {
  line: CartLine;
  currency: CurrencyCode;
  onIncrement: () => void;
  onDecrement: () => void;
  onRemove: () => void;
}) {
  const { line } = props;
  const [dx, setDx] = useState(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const axis = useRef<"none" | "x" | "y">("none");

  const end = () => {
    const remove = dx <= -SWIPE_REMOVE_PX;
    start.current = null;
    axis.current = "none";
    setDx(0);
    if (remove) props.onRemove();
  };

  const mods = line.modifiers.map((m) => m.label).join(", ");
  return (
    <li className="sell__line" data-testid={`line-${line.key}`}>
      <div className="sell__line-remove" aria-hidden="true" style={{ opacity: Math.min(1, -dx / SWIPE_REMOVE_PX) }}>
        Remove
      </div>
      <div
        className="sell__line-inner"
        style={{ transform: dx ? `translateX(${dx}px)` : undefined }}
        onPointerDown={(e) => {
          if (e.pointerType === "mouse" && e.button !== 0) return;
          start.current = { x: e.clientX, y: e.clientY };
          axis.current = "none";
        }}
        onPointerMove={(e) => {
          const s = start.current;
          if (!s) return;
          const moveX = e.clientX - s.x;
          const moveY = e.clientY - s.y;
          if (axis.current === "none") {
            if (Math.abs(moveX) < 8 && Math.abs(moveY) < 8) return;
            axis.current = Math.abs(moveX) > Math.abs(moveY) ? "x" : "y";
          }
          if (axis.current === "x") setDx(Math.min(0, moveX));
        }}
        onPointerUp={end}
        onPointerCancel={end}
      >
        <button
          type="button"
          className="sell__line-tap"
          aria-label={`${line.labelSnapshot}, quantity ${line.qty}. Add one more`}
          onClick={() => {
            if (axis.current === "x" || dx !== 0) return;
            props.onIncrement();
          }}
        >
          <span className="sell__line-qty" aria-hidden="true">
            {line.qty}
          </span>
          <span className="sell__line-text">
            <span className="sell__line-label">{line.labelSnapshot}</span>
            {mods ? <span className="sell__line-mods"> {mods}</span> : null}
          </span>
          <span className="sell__line-total">{formatMinor(lineTotalMinor(line), props.currency)}</span>
        </button>
        <button
          type="button"
          className="sell__line-minus"
          aria-label={`Remove one ${line.labelSnapshot}`}
          onClick={props.onDecrement}
        >
          −
        </button>
      </div>
    </li>
  );
}

/**
 * Persistent bottom panel (F-402): line items, then two action rows — Start Over (F-408), + Tip (D-063) and
 * + Customer, with a full-width Checkout (F-403) beneath them. The running total moved to the header
 * (D-051). Empty carts still show the bar so the buttons never move under the vendor's thumb.
 */
export function CartPanel(props: {
  lines: CartLine[];
  currency: CurrencyCode;
  onIncrement: (key: string) => void;
  onDecrement: (key: string) => void;
  onRemove: (key: string) => void;
  onClear: () => void;
  onCharge: () => void;
  /** Tip on this sale, 0 for none (D-063). */
  tipMinor: number;
  /** Opens the TipSheet. Only offered once the cart has items, like Start Over. */
  onTip: () => void;
  /** Display name of the customer picked for this sale, or null when none is picked (F-901–905). */
  customerName: string | null;
  /** Opens the CustomerPicker sheet; reachable any time while building the cart, not gating Cash/QR Pay. */
  onPickCustomer: () => void;
}) {
  return (
    <section className="sell__cart" aria-label="Current sale">
      <ul className="sell__cart-lines">
        {props.lines.map((l) => (
          <SwipeLine
            key={l.key}
            line={l}
            currency={props.currency}
            onIncrement={() => props.onIncrement(l.key)}
            onDecrement={() => props.onDecrement(l.key)}
            onRemove={() => props.onRemove(l.key)}
          />
        ))}
      </ul>
      <div className="sell__cart-bar">
        <div className="sell__cart-actions">
          {props.lines.length > 0 ? (
            <>
              <button type="button" className="sell__btn sell__btn--quiet sell__btn--start-over" onClick={props.onClear}>
                <span className="sell__btn-label sell__btn-label--words">Start Over</span>
              </button>
              <button
                type="button"
                className="sell__btn sell__btn--quiet sell__btn--tip"
                onClick={props.onTip}
              >
                {/* A set tip stacks "TIP" over the amount, so the button stays about as narrow as "+ Tip" and
                    + Customer keeps its one-line label (D-060). The space keeps the name "Tip $1.50". */}
                <span className="sell__btn-label sell__btn-label--words">
                  {props.tipMinor > 0 ? (
                    <>
                      <span className="sell__btn-kicker">Tip</span> {formatMinor(props.tipMinor, props.currency)}
                    </>
                  ) : (
                    "+ Tip"
                  )}
                </span>
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="sell__btn sell__btn--quiet sell__btn--customer"
            onClick={props.onPickCustomer}
            title={props.customerName ?? undefined}
          >
            <span className={props.customerName ? "sell__btn-label" : "sell__btn-label sell__btn-label--words"}>
              {props.customerName ?? "+ Customer"}
            </span>
          </button>
        </div>
        <button
          type="button"
          className="sell__btn sell__btn--block sell__btn--checkout"
          disabled={props.lines.length === 0}
          onClick={props.onCharge}
        >
          Checkout
        </button>
      </div>
    </section>
  );
}
